import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { PageLoader } from "./components/PageLoader";
import { initPhoneClickTracking } from "./lib/tracking";

// Critical pages loaded immediately
import { HomePage } from "./pages/HomePage";
import { NotFoundPage } from "./pages/NotFoundPage";

// Lazy load all other pages for better initial load performance
const TeamPage = lazy(() => import("./pages/TeamPage").then(m => ({ default: m.TeamPage })));
const ContactPage = lazy(() => import("./pages/ContactPage").then(m => ({ default: m.ContactPage })));
const MedicalBilling = lazy(() => import("./pages/MedicalBilling").then(m => ({ default: m.MedicalBilling })));
const Credentialing = lazy(() => import("./pages/Credentialing").then(m => ({ default: m.Credentialing })));
const CredentialingTexas = lazy(() => import("./pages/CredentialingTexas").then(m => ({ default: m.CredentialingTexas })));
const Consulting = lazy(() => import("./pages/Consulting").then(m => ({ default: m.Consulting })));
const WhoWeServe = lazy(() => import("./pages/WhoWeServe").then(m => ({ default: m.WhoWeServe })));
const Anesthesiology = lazy(() => import("./pages/Anesthesiology").then(m => ({ default: m.Anesthesiology })));
const Dermatology = lazy(() => import("./pages/Dermatology").then(m => ({ default: m.Dermatology })));
const Hospitalist = lazy(() => import("./pages/Hospitalist").then(m => ({ default: m.Hospitalist })));
const MentalHealth = lazy(() => import("./pages/MentalHealth").then(m => ({ default: m.MentalHealth })));
const OBGYN = lazy(() => import("./pages/OBGYN").then(m => ({ default: m.OBGYN })));
const Oncology = lazy(() => import("./pages/Oncology").then(m => ({ default: m.Oncology })));
const Orthopedic = lazy(() => import("./pages/Orthopedic").then(m => ({ default: m.Orthopedic })));
const Pathology = lazy(() => import("./pages/Pathology").then(m => ({ default: m.Pathology })));
const PlasticSurgery = lazy(() => import("./pages/PlasticSurgery").then(m => ({ default: m.PlasticSurgery })));
const Blog = lazy(() => import("./pages/Blog").then(m => ({ default: m.Blog })));
const BlogGroupNpiVsIndividualNpi = lazy(() => import("./pages/BlogGroupNpiVsIndividualNpi").then(m => ({ default: m.BlogGroupNpiVsIndividualNpi })));
const FAQs = lazy(() => import("./pages/FAQs").then(m => ({ default: m.FAQs })));
const Careers = lazy(() => import("./pages/Careers").then(m => ({ default: m.Careers })));
const CaseStudies = lazy(() => import("./pages/CaseStudies").then(m => ({ default: m.CaseStudies })));
const Newsletter = lazy(() => import("./pages/Newsletter").then(m => ({ default: m.Newsletter })));
const PracticeAudit = lazy(() => import("./pages/PracticeAudit").then(m => ({ default: m.PracticeAudit })));
const Services = lazy(() => import("./pages/Services").then(m => ({ default: m.Services })));
const RevenueCycleManagement = lazy(() => import("./pages/RevenueCycleManagement").then(m => ({ default: m.RevenueCycleManagement })));
const BlogBestMedicalBillingSoftware = lazy(() => import("./pages/BlogBestMedicalBillingSoftware").then(m => ({ default: m.BlogBestMedicalBillingSoftware })));
const BlogChangeHealthcareAttack = lazy(() => import("./pages/BlogChangeHealthcareAttack").then(m => ({ default: m.BlogChangeHealthcareAttack })));
const BlogDermatologyBillingChallenges = lazy(() => import("./pages/BlogDermatologyBillingChallenges").then(m => ({ default: m.BlogDermatologyBillingChallenges })));
const BlogFiveCodingMistakes = lazy(() => import("./pages/BlogFiveCodingMistakes").then(m => ({ default: m.BlogFiveCodingMistakes })));
const BlogInNetworkVsOutOfNetwork = lazy(() => import("./pages/BlogInNetworkVsOutOfNetwork").then(m => ({ default: m.BlogInNetworkVsOutOfNetwork })));
const BlogOutsourcingMedicalBilling = lazy(() => import("./pages/BlogOutsourcingMedicalBilling").then(m => ({ default: m.BlogOutsourcingMedicalBilling })));
const BlogServiceVsBillingAddress = lazy(() => import("./pages/BlogServiceVsBillingAddress").then(m => ({ default: m.BlogServiceVsBillingAddress })));
const BlogSwitchingBillingProviders = lazy(() => import("./pages/BlogSwitchingBillingProviders").then(m => ({ default: m.BlogSwitchingBillingProviders })));
const BlogWinYourAppeal = lazy(() => import("./pages/BlogWinYourAppeal").then(m => ({ default: m.BlogWinYourAppeal })));
const CaseStudyArise = lazy(() => import("./pages/CaseStudyArise").then(m => ({ default: m.CaseStudyArise })));
const CaseStudyIntegratedTherapy = lazy(() => import("./pages/CaseStudyIntegratedTherapy").then(m => ({ default: m.CaseStudyIntegratedTherapy })));
const CaseStudyChoiceCare = lazy(() => import("./pages/CaseStudyChoiceCare").then(m => ({ default: m.CaseStudyChoiceCare })));
const AdminCallLog = lazy(() => import("./pages/AdminCallLog"));
const AdminGSCDashboard = lazy(() => import("./pages/AdminGSCDashboard"));

// New SEO Landing Pages & Pillar Content
const MedicalBillingGuide = lazy(() => import("./pages/MedicalBillingGuide").then(m => ({ default: m.MedicalBillingGuide })));
const CredentialingGuide = lazy(() => import("./pages/CredentialingGuide").then(m => ({ default: m.CredentialingGuide })));
const MedicalBillingAtlanta = lazy(() => import("./pages/MedicalBillingAtlanta").then(m => ({ default: m.MedicalBillingAtlanta })));
const MedicalBillingSmallPractice = lazy(() => import("./pages/MedicalBillingSmallPractice").then(m => ({ default: m.MedicalBillingSmallPractice })));

// Additional Service & Specialty Pages
const DenialManagement = lazy(() => import("./pages/DenialManagement").then(m => ({ default: m.DenialManagement })));
const PhysicalTherapyBilling = lazy(() => import("./pages/PhysicalTherapyBilling").then(m => ({ default: m.PhysicalTherapyBilling })));

// State Landing Pages
const MedicalBillingTexas = lazy(() => import("./pages/MedicalBillingTexas").then(m => ({ default: m.MedicalBillingTexas })));
const MedicalBillingCalifornia = lazy(() => import("./pages/MedicalBillingCalifornia").then(m => ({ default: m.MedicalBillingCalifornia })));
const MedicalBillingNorthCarolina = lazy(() => import("./pages/MedicalBillingNorthCarolina").then(m => ({ default: m.MedicalBillingNorthCarolina })));

// FAQ & Glossary Hub
const QuestionsWeAnswer = lazy(() => import("./pages/QuestionsWeAnswer").then(m => ({ default: m.QuestionsWeAnswer })));

// Georgia Landing Page (Home State)
const MedicalBillingGeorgia = lazy(() => import("./pages/MedicalBillingGeorgia").then(m => ({ default: m.MedicalBillingGeorgia })));

// New Blog Articles
const BlogReduceClaimDenials = lazy(() => import("./pages/BlogReduceClaimDenials").then(m => ({ default: m.BlogReduceClaimDenials })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("./pages/TermsOfService").then(m => ({ default: m.TermsOfService })));
const HipaaCompliance = lazy(() => import("./pages/HipaaCompliance").then(m => ({ default: m.HipaaCompliance })));
const Accessibility = lazy(() => import("./pages/Accessibility").then(m => ({ default: m.Accessibility })));
const BlogMedicalBillingKPIs = lazy(() => import("./pages/BlogMedicalBillingKPIs").then(m => ({ default: m.BlogMedicalBillingKPIs })));

const LEGACY_REDIRECTS = {
  '/index.php': '/',
  '/index.html': '/',
  '/home.php': '/',
  '/home': '/',
  '/services.php': '/services',
  '/our-services.php': '/services',
  '/our-services': '/services',
  '/medical-billing.php': '/medical-billing',
  '/billing-services.php': '/medical-billing',
  '/billing.php': '/medical-billing',
  '/credentialing.php': '/credentialing',
  '/credentialing-services.php': '/credentialing',
  '/provider-credentialing.php': '/credentialing',
  '/credentialing-services-texas.php': '/credentialing-texas',
  '/revenue-cycle-management.php': '/revenue-cycle-management',
  '/rcm.php': '/revenue-cycle-management',
  '/rcm-services.php': '/revenue-cycle-management',
  '/practice-audit.php': '/practice-audit',
  '/audit.php': '/practice-audit',
  '/medical-practice-audit.php': '/practice-audit',
  '/contact.php': '/contact',
  '/contact-us.php': '/contact',
  '/contact-us': '/contact',
  '/contactus.php': '/contact',
  '/team.php': '/team',
  '/our-team.php': '/team',
  '/about.php': '/team',
  '/about-us.php': '/team',
  '/about-us': '/team',
  '/about': '/team',
  '/consulting.php': '/consulting',
  '/consulting-services.php': '/consulting',
  '/careers.php': '/careers',
  '/jobs.php': '/careers',
  '/employment.php': '/careers',
  '/newsletter.php': '/newsletter',
  '/newsletters.php': '/newsletter',
  '/blog.php': '/blog',
  '/anesthesiology.php': '/anesthesiology',
  '/anesthesiology-billing.php': '/anesthesiology',
  '/anesthesia-billing.php': '/anesthesiology',
  '/dermatology.php': '/dermatology',
  '/dermatology-billing.php': '/dermatology',
  '/hospitalist.php': '/hospitalist',
  '/hospitalist-billing.php': '/hospitalist',
  '/hospitalist-medical-billing-services.php': '/hospitalist',
  '/mental-health.php': '/mental-health',
  '/psychiatry.php': '/mental-health',
  '/psychiatry-billing.php': '/mental-health',
  '/mental-health-billing.php': '/mental-health',
  '/obgyn.php': '/obgyn',
  '/ob-gyn.php': '/obgyn',
  '/obstetrics.php': '/obgyn',
  '/gynecology.php': '/obgyn',
  '/oncology.php': '/oncology',
  '/oncology-billing.php': '/oncology',
  '/cancer-billing.php': '/oncology',
  '/orthopedic.php': '/orthopedic',
  '/orthopedics.php': '/orthopedic',
  '/orthopedic-billing.php': '/orthopedic',
  '/pathology.php': '/pathology',
  '/pathology-billing.php': '/pathology',
  '/lab-billing.php': '/pathology',
  '/plastic-surgery.php': '/plastic-surgery',
  '/plastics.php': '/plastic-surgery',
  '/cosmetic-surgery.php': '/plastic-surgery',
  '/who-we-serve.php': '/who-we-serve',
  '/specialties.php': '/who-we-serve',
  '/faq.php': '/faqs',
  '/faqs.php': '/faqs',
  '/frequently-asked-questions.php': '/faqs',
  '/case-studies.php': '/case-studies',
  '/success-stories.php': '/case-studies',
  '/billing-under-group-npi-vs-individual-npi.php': '/blog/group-npi-vs-individual-npi',
  '/service-address-vs-billing-address.php': '/blog/service-vs-billing-address',
  '/medical-billing-in-network-vs-out-of-network.php': '/blog/in-network-vs-out-of-network',
  '/outsourcing-medical-billing.php': '/blog/outsourcing-medical-billing',
  '/switching-billing-providers.php': '/blog/switching-billing-providers',
  '/best-medical-billing-software.php': '/blog/best-medical-billing-software',
  '/dermatology-medical-billing-challenges-and-tips.php': '/blog/dermatology-billing-challenges',
  '/navigating-the-storm-change-healthcare.php': '/blog/change-healthcare-cyberattack',
  '/five-medical-coding-mistakes.php': '/blog/five-medical-coding-mistakes',
  '/win-your-appeal.php': '/blog/win-your-appeal',
  '/arise-psychiatric-medical.php': '/case-studies/arise-psychiatric-medical',
  '/integrated-therapy-llc.php': '/case-studies/integrated-therapy-llc',
  '/choice-care-occupational-medicine.php': '/case-studies/choice-care-occupational-medicine',
};

function LegacyRedirect() {
  const location = useLocation();
  const newPath = LEGACY_REDIRECTS[location.pathname];
  if (newPath) {
    return <Navigate to={newPath} replace />;
  }
  return <NotFoundPage />;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => initPhoneClickTracking(), []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <a href="#main-content" className="skip-nav" data-testid="skip-nav">
          Skip to main content
        </a>
        <ScrollToTop />
        <Header />
        <main id="main-content" role="main">
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/medical-billing" element={<MedicalBilling />} />
          <Route path="/credentialing" element={<Credentialing />} />
          <Route path="/credentialing-texas" element={<CredentialingTexas />} />
          <Route path="/consulting" element={<Consulting />} />
          <Route path="/who-we-serve" element={<WhoWeServe />} />
          <Route path="/anesthesiology" element={<Anesthesiology />} />
          <Route path="/dermatology" element={<Dermatology />} />
          <Route path="/hospitalist" element={<Hospitalist />} />
          <Route path="/mental-health" element={<MentalHealth />} />
          <Route path="/obgyn" element={<OBGYN />} />
          <Route path="/oncology" element={<Oncology />} />
          <Route path="/orthopedic" element={<Orthopedic />} />
          <Route path="/pathology" element={<Pathology />} />
          <Route path="/plastic-surgery" element={<PlasticSurgery />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/group-npi-vs-individual-npi" element={<BlogGroupNpiVsIndividualNpi />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/newsletter" element={<Newsletter />} />
          <Route path="/practice-audit" element={<PracticeAudit />} />
          <Route path="/services" element={<Services />} />
          <Route path="/revenue-cycle-management" element={<RevenueCycleManagement />} />
          <Route path="/blog/best-medical-billing-software" element={<BlogBestMedicalBillingSoftware />} />
          <Route path="/blog/change-healthcare-cyberattack" element={<BlogChangeHealthcareAttack />} />
          <Route path="/blog/dermatology-billing-challenges" element={<BlogDermatologyBillingChallenges />} />
          <Route path="/blog/five-medical-coding-mistakes" element={<BlogFiveCodingMistakes />} />
          <Route path="/blog/in-network-vs-out-of-network" element={<BlogInNetworkVsOutOfNetwork />} />
          <Route path="/blog/outsourcing-medical-billing" element={<BlogOutsourcingMedicalBilling />} />
          <Route path="/blog/service-vs-billing-address" element={<BlogServiceVsBillingAddress />} />
          <Route path="/blog/switching-billing-providers" element={<BlogSwitchingBillingProviders />} />
          <Route path="/blog/win-your-appeal" element={<BlogWinYourAppeal />} />
          <Route path="/case-studies/arise-psychiatric-medical" element={<CaseStudyArise />} />
          <Route path="/case-studies/integrated-therapy-llc" element={<CaseStudyIntegratedTherapy />} />
          <Route path="/case-studies/choice-care-occupational-medicine" element={<CaseStudyChoiceCare />} />
          <Route path="/admin/calls" element={<AdminCallLog />} />
          <Route path="/admin/search-console" element={<AdminGSCDashboard />} />
          {/* SEO Landing Pages & Pillar Content */}
          <Route path="/medical-billing-guide" element={<MedicalBillingGuide />} />
          <Route path="/credentialing-guide" element={<CredentialingGuide />} />
          <Route path="/medical-billing-atlanta" element={<MedicalBillingAtlanta />} />
          <Route path="/medical-billing-small-practice" element={<MedicalBillingSmallPractice />} />
          {/* Additional Service & Specialty Pages */}
          <Route path="/denial-management" element={<DenialManagement />} />
          <Route path="/physical-therapy-billing" element={<PhysicalTherapyBilling />} />
          {/* State Landing Pages */}
          <Route path="/medical-billing-texas" element={<MedicalBillingTexas />} />
          <Route path="/medical-billing-california" element={<MedicalBillingCalifornia />} />
          <Route path="/medical-billing-north-carolina" element={<MedicalBillingNorthCarolina />} />
          {/* FAQ & Glossary Hub */}
          <Route path="/questions-we-answer" element={<QuestionsWeAnswer />} />
          {/* Georgia Landing Page */}
          <Route path="/medical-billing-georgia" element={<MedicalBillingGeorgia />} />
          {/* New Blog Articles */}
          <Route path="/blog/reduce-claim-denials" element={<BlogReduceClaimDenials />} />
          <Route path="/blog/medical-billing-kpis" element={<BlogMedicalBillingKPIs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/hipaa-compliance" element={<HipaaCompliance />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="*" element={<LegacyRedirect />} />
        </Routes>
        </Suspense>
        </main>
        <Footer />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
